import {
  EMPLOYEE_BANDS,
  HIGHER_RISK_ACTIVITIES,
  INDUSTRIES,
  REVENUE_BANDS,
  REVENUE_LABEL,
  TriageAnswers,
  intakeConsentText,
  revenueBand,
  websiteDomain,
  type HigherRiskActivity,
  type PublicIntakeLink,
} from '@cyberscore/contracts';
import { CheckCircle2, Loader2, ShieldCheck } from 'lucide-react';
import { useEffect, useId, useState, type CSSProperties, type ReactNode } from 'react';
import { ApiError } from '../../lib/api';
import { publicLink, submitIntake } from './api';

const EMPTY = { business_name: '', contact_name: '', contact_email: '', phone: '', website: '', industry: '', country: 'MY', employees: '', revenue: '', card_payments: '', personal_data: '', sum_insured: '', renewal_date: '', notes: '', revenue_amount: '', overseas_revenue: '' }; // prettier-ignore
type Form = Record<keyof typeof EMPTY, string>;
const amount = (v: string) => Number(v.replace(/[,\s]|^RM/gi, ''));
const MISSING: Record<string, string> = { business_name: 'Give the business’s name', contact_name: 'Give your name', contact_email: 'An email address we can reach you at' }; // prettier-ignore

/**
 * The public submission form (task 68): a business tells an insurer or broker about itself, in that
 * insurer's or broker's name and colour. Anyone with the link can open it, signed in or not.
 */
export function PublicIntakePage({ token }: { token: string }) {
  const [link, setLink] = useState<PublicIntakeLink | null | 'missing'>(null);
  const [f, setF] = useState<Form>(EMPTY);
  const [fax, setFax] = useState('');
  const [checkDomain, setCheckDomain] = useState(false);
  const [activities, setActivities] = useState<HigherRiskActivity[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [banner, setBanner] = useState<string | null>(null);
  const [more, setMore] = useState(false);

  useEffect(() => {
    publicLink(token).then(setLink, () => setLink('missing'));
  }, [token]);
  useEffect(() => {
    if (link && link !== 'missing') document.title = `${link.brand_name} · ${link.heading}`;
  }, [link]);

  const set = (k: keyof Form, v: string) => setF((x) => ({ ...x, [k]: v }));
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const raw = Object.fromEntries(Object.entries(f).filter(([, v]) => v.trim() !== ''));
    // A link that prepares insurers' forms asks revenue in RM, which decides the band (task 90).
    const placement = link && link !== 'missing' && link.placement;
    const rm = placement && raw.revenue_amount ? amount(raw.revenue_amount) : undefined;
    const extra = placement
      ? { revenue_amount: rm, revenue: rm !== undefined && Number.isFinite(rm) && rm >= 0 ? revenueBand(rm) : undefined, activities: activities.length ? activities : undefined } // prettier-ignore
      : { revenue_amount: undefined, overseas_revenue: undefined };
    const parsed = TriageAnswers.safeParse({ ...raw, sum_insured: raw.sum_insured ? amount(raw.sum_insured) : undefined, ...extra }); // prettier-ignore
    const required = placement ? { revenue_amount: !raw.revenue_amount && 'Give the yearly revenue in RM', overseas_revenue: !raw.overseas_revenue && 'Choose one', activities: !activities.length && 'Tick what applies, or “None of these”' } : {}; // prettier-ignore
    const missing = Object.entries(required).filter(([, m]) => m) as [string, string][];
    if (!parsed.success || missing.length) {
      const issues = parsed.success ? [] : parsed.error.issues.map((i) => { const k = String(i.path[0]); return [k === 'revenue' && placement ? 'revenue_amount' : k, !raw[k] ? (MISSING[k] ?? 'Choose one') : i.code === 'invalid_value' ? 'Choose one' : i.message]; }); // prettier-ignore
      setErrors(Object.fromEntries([...issues, ...missing]));
      return;
    }
    setErrors({});
    setSending(true);
    setBanner(null);
    try {
      await submitIntake(token, parsed.data, fax, checkDomain && !!websiteDomain(parsed.data.website));
      setSent(true);
    } catch (err) {
      setBanner(err instanceof ApiError ? err.message : 'Could not send. Try again in a moment.');
    } finally {
      setSending(false);
    }
  };

  if (link === null)
    return (
      <div className="intake-page">
        <p className="muted intake-loading">Loading…</p>
      </div>
    );
  if (link === 'missing')
    return (
      <div className="intake-page">
        <main className="intake-card">
          <h1>This form isn’t taking submissions</h1>
          <p className="muted">The link may have been turned off. Ask whoever sent it for a new one.</p>
        </main>
      </div>
    ); // prettier-ignore

  const style = { '--brand': link.accent } as CSSProperties;
  const domain = websiteDomain(f.website);
  // Opened by hand, or by a problem in one of its answers.
  const showMore = more || ['website', 'sum_insured', 'renewal_date', 'notes'].some((k) => errors[k]);
  return (
    <div className="intake-page" style={style}>
      <header className="intake-head">
        <div className="intake-brand"><ShieldCheck size={18} aria-hidden /> {link.brand_name}</div>
        <h1>{link.heading}</h1>
        {link.intro && <p>{link.intro}</p>}
      </header>
      <main className="intake-card">
        {sent ? (
          <div className="intake-done" role="status">
            <CheckCircle2 size={36} aria-hidden />
            <h2>Thank you</h2>
            <p>{link.brand_name} has your details and will email {f.contact_email} about the next step.</p>
          </div>
        ) : (
          <form onSubmit={submit} noValidate>
            {banner && <div className="notice" role="alert">{banner}</div>}
            <fieldset>
              <legend>About the business</legend>
              <Q label="Business name" error={errors.business_name}>{(id) => <input id={id} className="input" autoComplete="organization" value={f.business_name} onChange={(e) => set('business_name', e.target.value)} />}</Q>
              <div className="two">
                <Q label="Industry" error={errors.industry}>{(id) => <Pick id={id} value={f.industry} onChange={(v) => set('industry', v)} options={Object.entries(INDUSTRIES)} />}</Q>
                <Q label="Country" error={errors.country}>{(id) => <Pick id={id} value={f.country} onChange={(v) => set('country', v)} options={[['MY', 'Malaysia'], ['SG', 'Singapore'], ['ID', 'Indonesia'], ['TH', 'Thailand']]} />}</Q>
              </div>
              <div className="two">
                <Q label="People working there" error={errors.employees}>{(id) => <Pick id={id} value={f.employees} onChange={(v) => set('employees', v)} options={EMPLOYEE_BANDS.map((b) => [b, b])} />}</Q>
                {link.placement
                  ? <Q label="Yearly revenue (RM)" error={errors.revenue_amount}>{(id) => <input id={id} className="input" inputMode="decimal" placeholder="e.g. 8,500,000" value={f.revenue_amount} onChange={(e) => set('revenue_amount', e.target.value)} />}</Q>
                  : <Q label="Yearly revenue" error={errors.revenue}>{(id) => <Pick id={id} value={f.revenue} onChange={(v) => set('revenue', v)} options={REVENUE_BANDS.map((b) => [b, REVENUE_LABEL[b]])} />}</Q>}
              </div>
              <div className="two">
                <Q label="Do you take card payments?" error={errors.card_payments}>{(id) => <Pick id={id} value={f.card_payments} onChange={(v) => set('card_payments', v)} options={[['yes', 'Yes'], ['no', 'No']]} />}</Q>
                <Q label="Do you keep customers’ personal data?" error={errors.personal_data}>{(id) => <Pick id={id} value={f.personal_data} onChange={(v) => set('personal_data', v)} options={[['yes', 'Yes'], ['no', 'No']]} />}</Q>
              </div>
            </fieldset>
            {link.placement && (
              <fieldset>
                <legend>For the insurers</legend>
                <Q label="Does any revenue come from customers outside Malaysia?" error={errors.overseas_revenue}>{(id) => <Pick id={id} value={f.overseas_revenue} onChange={(v) => set('overseas_revenue', v)} options={[['no', 'No, all of it is from Malaysia'], ['yes', 'Yes']]} />}</Q>
                <div className="field" role="group" aria-labelledby="intake-activities">
                  <span id="intake-activities" className="field-label">Does the business do any of these?</span>
                  <div>
                  {(Object.entries(HIGHER_RISK_ACTIVITIES) as [HigherRiskActivity, string][]).map(([v, l]) => (
                    <label key={v} className="toggle-row">
                      <input type="checkbox" checked={activities.includes(v)} onChange={(e) => setActivities((a) => (!e.target.checked ? a.filter((x) => x !== v) : v === 'none' ? ['none'] : [...a.filter((x) => x !== 'none'), v]))} /> {l}
                    </label>
                  ))}
                  </div>
                  {errors.activities && <span className="error">{errors.activities}</span>}
                </div>
              </fieldset>
            )}
            <fieldset>
              <legend>Who we should contact</legend>
              <Q label="Your name" error={errors.contact_name}>{(id) => <input id={id} className="input" autoComplete="name" value={f.contact_name} onChange={(e) => set('contact_name', e.target.value)} />}</Q>
              <div className="two">
                <Q label="Email" error={errors.contact_email}>{(id) => <input id={id} className="input" type="email" autoComplete="email" value={f.contact_email} onChange={(e) => set('contact_email', e.target.value)} />}</Q>
                <Q label="Phone (optional)" error={errors.phone}>{(id) => <input id={id} className="input" type="tel" autoComplete="tel" value={f.phone} onChange={(e) => set('phone', e.target.value)} />}</Q>
              </div>
            </fieldset>
            {/* Only what picks the check is asked up front (task 111); the rest can wait, or be added here. */}
            {!showMore ? (
              <button type="button" className="link-btn intake-more" aria-expanded="false" onClick={() => setMore(true)}>
                Add more details (optional): your website, the cover you want
              </button>
            ) : (
              <>
                <fieldset>
                  <legend>Your website (optional)</legend>
                  <Q label="Website (optional)" error={errors.website}>{(id) => <input id={id} className="input" inputMode="url" placeholder="example.com.my" value={f.website} onChange={(e) => set('website', e.target.value)} />}</Q>
                  {domain && (
                    <label className="intake-consent">
                      <input type="checkbox" checked={checkDomain} onChange={(e) => setCheckDomain(e.target.checked)} />
                      <span><b>Optional: a first look at your domain.</b> {intakeConsentText({ business: f.business_name.trim() || 'my business', brand: link.brand_name, domain })}</span>
                    </label>
                  )}
                </fieldset>
            <fieldset>
              <legend>The cover you want (optional)</legend>
              <div className="two">
                <Q label="Sum insured (RM)" error={errors.sum_insured}>{(id) => <input id={id} className="input" inputMode="decimal" value={f.sum_insured} onChange={(e) => set('sum_insured', e.target.value)} />}</Q>
                <Q label="Current policy renews on" error={errors.renewal_date}>{(id) => <input id={id} className="input" type="date" value={f.renewal_date} onChange={(e) => set('renewal_date', e.target.value)} />}</Q>
              </div>
              <Q label="Anything else we should know" error={errors.notes}>{(id) => <textarea id={id} className="input" rows={3} maxLength={1000} value={f.notes} onChange={(e) => set('notes', e.target.value)} />}</Q>
            </fieldset>
              </>
            )}
            {/* Hidden from people; a bot that fills it is ignored. */}
            <input className="intake-hp" tabIndex={-1} autoComplete="off" aria-hidden="true" name="company_fax" value={fax} onChange={(e) => setFax(e.target.value)} />
            <p className="muted small">We use these details only to offer you cover and set up your cyber check. They go to {link.brand_name}.</p>
            <button type="submit" className="btn btn-primary intake-submit" disabled={sending}>{sending && <Loader2 size={15} className="spin" />}Send to {link.brand_name}</button>
          </form>
        )}
      </main>
      <p className="muted small intake-foot">Powered by CyberScore</p>
    </div>
  ); // prettier-ignore
}

function Q({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: (id: string) => ReactNode;
}) {
  const id = useId();
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      {children(id)}
      {error && <span className="error">{error}</span>}
    </div>
  );
}

function Pick({
  id,
  value,
  onChange,
  options,
}: {
  id: string;
  value: string;
  onChange: (v: string) => void;
  options: readonly (readonly [string, string])[];
}) {
  // prettier-ignore
  return (
    <select id={id} className="input" value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">Choose…</option>
      {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  ); // prettier-ignore
}
